// pages/ApplicationPage.jsx
/**
 * ApplicationPage (카카오톡 알림 신청/해제 페이지)
 * --------------------------------------------------------------------
 * 목적:
 *  - 현재 매장이 카카오톡 알림 서비스에 "신청됨/미신청" 상태인지 조회하고,
 *    사용자의 입력(전화번호)으로 신청/해제를 처리한다.
 *  - 설명/유의사항 모달, 확인 모달, 취소(해제) 모달, 토스트 등의 UX를 포함한다.
 *
 * 데이터 흐름:
 *  - 마운트 시 /api/application/kakao GET으로 현재 신청 여부/전화번호 조회.
 *  - "신청하기" → 전화번호 입력 모달(PhoneModal) → 확인 모달(ConfirmModal)
 *      → 최종 확인 시 /api/application/kakao POST (params: { phoneNumber })
 *  - "신청 해제" → 해제 확인 모달(CancelModal)
 *      → 최종 확인 시 /api/application/kakao DELETE
 *  - 성공 시 상태 갱신 + 토스트(AppToast) 노출.
 *
 * 인증:
 *  - 세션스토리지 accessToken을 mokiApi Authorization 헤더로 세팅하여 호출.
 *
 */

import React, { useState } from "react";
import Appcard from "../../component/common/Appcard";
import PhoneModal from "../../component/common/PhoneModal";
import ConfirmModal from "../../component/common/ConfirmModal";
import AppToast from "../../component/common/AppToast";
import NoticeModal from "../../component/common/NoticeModal";
import CancelModal from "../../component/common/CancleModal"; // ⚠️ "Cancel" 철자 확인 필요
import { mokiApi } from "../../services/loginApi";
import { useEffect } from "react";

function ApplicationPage(props) {
  // 신청 상태: true(신청됨) / false(미신청)
  const [isRegistered, setIsRegistered] = useState(false);
  // 신청에 사용할 전화번호(표시/확인용)
  const [phoneNumber, setPhoneNumber] = useState("");
  // 모달/토스트 UI 상태
  const [showPhoneModal, setShowPhoneModal] = useState(false); // 전화번호 입력
  const [showConfirm, setShowConfirm] = useState(false); // 최종 확인(등록)
  const [showToast, setShowToast] = useState(false); // 결과 토스트
  const [showNoticeModal, setShowNoticeModal] = useState(false); // 안내/상세 모달
  const [showCancelModal, setShowCancelModal] = useState(false); // 해제 확인 모달

  // 헤더/카드에서 사용할 스토어명
  const name = sessionStorage.getItem("name") || "가게 이름";

  useEffect(() => {
    // 1) 인증 헤더 세팅
    const token = sessionStorage.getItem("accessToken");
    if (token) {
      mokiApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // 2) 현재 신청 여부 조회
    (async () => {
      try {
        // 응답 스키마 가정: { is_applied: "true"|"false", phone_number: "010..." }
        const res = await mokiApi.get("/api/application/kakao");
        if (res.data.is_applied === "true") {
          setIsRegistered(true);
          setPhoneNumber(res.data.phone_number);
        } else {
          setIsRegistered(false);
          setPhoneNumber("");
        }
      } catch (e) {
        console.error("등록 여부 조회 실패", e);
        // 실패 시 UX: 조용히 실패(필요 시 에러 토스트/안내 추가 고려)
      }
    })();
  }, []);

  // [신청하기 / 신청 해제] 메인 버튼 클릭
  const handleApplyClick = () => {
    setShowToast(false); // 이전 토스트 숨김
    if (isRegistered) {
      // 이미 신청됨 → 해제 플로우
      setShowCancelModal(true);
    } else {
      // 미신청 → 전화번호 입력부터
      setShowPhoneModal(true);
    }
  };

  // [전화번호 입력 모달] 닫기/확인
  const handlePhoneClose = () => setShowPhoneModal(false);
  const handlePhoneConfirm = (enteredPhone) => {
    // 입력값 저장 후 확인 모달로 진행
    setPhoneNumber(enteredPhone);
    setShowPhoneModal(false);
    setShowConfirm(true);
  };

  // [최종 확인 모달] "이전" → 전화번호 재입력으로 돌아가기
  const handleConfirmBack = () => {
    setShowConfirm(false);
    setShowPhoneModal(true);
  };

  // [최종 확인 모달] "확인" → 등록 API 호출
  const handleFinalConfirm = async () => {
    try {
      // 서버는 phoneNumber를 querystring(params)로 받는 계약
      const res = await mokiApi.post("/api/application/kakao", null, {
        params: { phoneNumber: phoneNumber },
      });
      if (res.data.message === "success") {
        // 성공: 상태 갱신 + 토스트
        setIsRegistered(true);
        setShowConfirm(false);
        setShowToast(true);
      } else {
        alert("등록에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (err) {
      console.error(err);
      alert("네트워크 오류가 발생했습니다.");
    }
  };

  // [해제 모달] "이전"
  const handleCancelBack = () => setShowCancelModal(false);

  // [해제 모달] "확인" → 해제 API 호출
  const handleCancelConfirm = async () => {
    try {
      const res = await mokiApi.delete("/api/application/kakao");
      if (res.data.message === "success") {
        // 성공: 신청 상태/전화번호 초기화 + 토스트
        setIsRegistered(false);
        setPhoneNumber("");
        setShowCancelModal(false);
        setShowToast(true);
      } else {
        alert("해제에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (err) {
      console.error(err);
      alert("네트워크 오류로 해제에 실패했습니다.");
    }
  };

  // [안내 보기] 버튼 → 상세/유의사항 모달
  const handleNoticeClick = () => {
    setShowToast(false);
    setShowNoticeModal(true);
  };
  const handleNoticeClose = () => setShowNoticeModal(false);

  return (
    <div className="flex flex-col min-h-screen relative p-5">
      {/* 상단 헤더(간단 로고/타이틀) */}
      <div className="flex items-center p-4 w-full bg-white z-10">
        <img
          src="/assets/report.png"
          alt="Moki Logo"
          className="w-[10vw] min-w-[40px]"
        />
        <div className="flex flex-col text-[4.5vw] m-[2vh] font-extrabold">
          <div>실시간 매출분석 데이터</div>
          <div>카카오톡 알림신청</div>
        </div>
      </div>

      {/* 콘텐츠 본문: 카드 + 상세보기 버튼(신청 상태일 때만) */}
      <div className="flex flex-col items-center flex-1 overflow-auto px-4 pt-10 pb-20">
        <p className="text-black font-extrabold text-[5vw]">{name}</p>
        <div className="flex flex-col items-center">
          <Appcard
            phone_number={isRegistered ? phoneNumber : "미설정"}
            is_registered={isRegistered ? "Y" : "N"}
            store_name={name}
          />
          <div>
            {isRegistered && (
              <div className="w-full flex justify-center">
                {/* 신청 상태일 때만 상세/유의사항 버튼 노출 */}
                <button
                  onClick={handleNoticeClick}
                  className="h-[5vh] w-[80vw] max-w-lg rounded-xl justify-center items-center text-blue-500 font-bold"
                >
                  상세보기{"  >"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 하단 고정 CTA: 신청하기 / 신청 해제 */}
      <div className="fixed bottom-6 left-0 w-full flex flex-col items-center gap-2">
        <div className="w-full flex justify-center">
          <button
            onClick={handleApplyClick}
            className={
              "h-[5vh] w-[80vw] max-w-lg rounded-lg font-bold mb-1 " +
              (isRegistered ? " text-red-400" : "bg-blue-500 text-white")
            }
          >
            {isRegistered ? "신청 해제" : "신청하기"}
          </button>
        </div>
      </div>

      {/* 전화번호 입력 모달 */}
      <PhoneModal
        open={showPhoneModal}
        initialPhone={phoneNumber}
        onClose={handlePhoneClose}
        onConfirm={handlePhoneConfirm}
      />

      {/* 최종 확인 모달(등록) */}
      <ConfirmModal
        open={showConfirm}
        storeName={name}
        phoneNumber={phoneNumber}
        onBack={handleConfirmBack}
        onConfirm={handleFinalConfirm}
      />

      {/* 알림 안내/상세 모달 */}
      <NoticeModal open={showNoticeModal} onClose={handleNoticeClose} />

      {/* 해제 확인 모달 */}
      <CancelModal
        open={showCancelModal}
        onBack={handleCancelBack}
        onConfirm={handleCancelConfirm}
      />

      {/* 결과 토스트: 신청/해제에 따라 문구 분기 */}
      {showToast && (
        <AppToast
          message={
            isRegistered
              ? "알림톡 등록 설정이 완료되었습니다."
              : "알림톡 등록이 해제되었습니다."
          }
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}

export default ApplicationPage;
